const LOG = '[growi-plugin-word-count]';
const BADGE_ID = 'growi-plugin-word-count-badge';
const STYLE_ID = 'growi-plugin-word-count-style';

// pdf-export と同じ候補セレクタ。GROWI のバージョン差異を吸収する。
const PAGE_CONTENT_SELECTORS = [
  '.main .revision-body',
  '.main .page-content',
  '.main .wiki',
  '#revision-body',
  '#page-content',
  '[data-testid="page-view"]',
  'main .wiki',
  'main',
];

// 一般的な読了速度 (英語: 200 wpm 程度, 日本語: 400-600 文字/分)。
// 日英混在を考慮して「文字数 / 500」と「英単語数 / 200」の合計で見積もる。
const CJK_CHARS_PER_MINUTE = 500;
const WORDS_PER_MINUTE = 200;

let bodyObserver: MutationObserver | null = null;
let contentObserver: MutationObserver | null = null;
let active = false;
let recalcTimer: number | null = null;

const findPageContent = (): HTMLElement | null => {
  for (const selector of PAGE_CONTENT_SELECTORS) {
    const el = document.querySelector<HTMLElement>(selector);
    if (el != null) return el;
  }
  return null;
};

interface Counts {
  characters: number;
  charactersNoSpace: number;
  cjkCharacters: number;
  words: number;
  readingMinutes: number;
}

const CJK_REGEX = /[぀-ヿ㐀-䶿一-鿿豈-﫿]/;
const ASCII_WORD_REGEX = /[A-Za-z0-9]+(?:[''\-][A-Za-z0-9]+)*/g;

const computeCounts = (text: string): Counts => {
  const characters = text.length;
  const charactersNoSpace = text.replace(/\s+/g, '').length;

  let cjkCharacters = 0;
  for (const ch of text) {
    if (CJK_REGEX.test(ch)) cjkCharacters++;
  }

  const words = (text.match(ASCII_WORD_REGEX) ?? []).length;

  const readingMinutes = Math.max(
    1,
    Math.ceil(cjkCharacters / CJK_CHARS_PER_MINUTE + words / WORDS_PER_MINUTE),
  );

  return { characters, charactersNoSpace, cjkCharacters, words, readingMinutes };
};

const formatBadge = (c: Counts): string => {
  // 文字 (空白除く) / 単語 / 読了時間 の 3 行表示
  return [
    `${c.charactersNoSpace.toLocaleString()} chars`,
    `${c.words.toLocaleString()} words`,
    `~${c.readingMinutes} min read`,
  ].join('\n');
};

const injectStyles = (): void => {
  if (document.getElementById(STYLE_ID) != null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    #${BADGE_ID} {
      position: fixed !important;
      left: 20px !important;
      bottom: 20px !important;
      z-index: 2147483646 !important;
      padding: 8px 12px !important;
      border-radius: 10px !important;
      background: rgba(20, 24, 36, 0.82) !important;
      color: #f5f7fb !important;
      font-size: 12px !important;
      line-height: 1.45 !important;
      font-weight: 500 !important;
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif !important;
      white-space: pre-line !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.22) !important;
      pointer-events: none !important;
      user-select: none !important;
      backdrop-filter: blur(6px);
      opacity: 0.92;
    }
  `;
  (document.head ?? document.documentElement).appendChild(style);
};

const ensureBadge = (): HTMLElement | null => {
  if (!active) return null;
  if (document.body == null) return null;
  injectStyles();
  let badge = document.getElementById(BADGE_ID);
  if (badge == null) {
    badge = document.createElement('div');
    badge.id = BADGE_ID;
    badge.setAttribute('aria-live', 'polite');
    document.body.appendChild(badge);
    console.info(`${LOG} badge mounted`);
  }
  return badge;
};

const recalc = (): void => {
  const badge = ensureBadge();
  if (badge == null) return;
  const target = findPageContent();
  if (target == null) {
    badge.textContent = 'no page content';
    return;
  }
  const text = (target.innerText ?? target.textContent ?? '').trim();
  const counts = computeCounts(text);
  badge.textContent = formatBadge(counts);
};

const scheduleRecalc = (): void => {
  if (recalcTimer != null) window.clearTimeout(recalcTimer);
  recalcTimer = window.setTimeout(() => {
    recalcTimer = null;
    recalc();
  }, 200);
};

const startContentObserver = (): void => {
  contentObserver?.disconnect();
  const target = findPageContent();
  if (target == null) return;
  contentObserver = new MutationObserver(() => {
    scheduleRecalc();
  });
  contentObserver.observe(target, {
    subtree: true,
    childList: true,
    characterData: true,
  });
};

const startBodyObserver = (): void => {
  if (bodyObserver != null) return;
  if (document.body == null) return;
  bodyObserver = new MutationObserver(() => {
    ensureBadge();
    startContentObserver();
    scheduleRecalc();
  });
  bodyObserver.observe(document.body, { childList: true, subtree: false });
};

const waitForBody = (cb: () => void): void => {
  if (document.body != null) {
    cb();
    return;
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cb, { once: true });
    return;
  }
  const id = window.setInterval(() => {
    if (document.body != null) {
      window.clearInterval(id);
      cb();
    }
  }, 50);
};

export const mountWordCountBadge = (): void => {
  active = true;
  console.info(`${LOG} activate (readyState=${document.readyState})`);
  waitForBody(() => {
    ensureBadge();
    startContentObserver();
    startBodyObserver();
    recalc();
  });
};

export const unmountWordCountBadge = (): void => {
  active = false;
  if (recalcTimer != null) {
    window.clearTimeout(recalcTimer);
    recalcTimer = null;
  }
  contentObserver?.disconnect();
  contentObserver = null;
  bodyObserver?.disconnect();
  bodyObserver = null;
  document.getElementById(BADGE_ID)?.remove();
  document.getElementById(STYLE_ID)?.remove();
  console.info(`${LOG} deactivate`);
};
